import { findTicketOption, getItemTotal } from './cart'
import { normaliseParticipantKey } from './format'

export const buildUpsellRequestItems = (items = []) => {
  return items.map((item) => ({
    attraction: item?.attraction,
    product_code: item?.product_code ?? '',
    time: item?.time ?? '',
    participants: item?.participants ?? {},
  }))
}

export const resolveUpsellId = (offer = {}) => Number(offer?.id ?? 0)

export const resolveUpsellTriggerIds = (offer = {}) => {
  if (Array.isArray(offer?.attractions) && offer.attractions.length > 0) {
    return offer.attractions
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0)
  }

  return []
}

export const resolveUpsellCartAttractionIds = (items = []) => {
  return items
    .map((item) => Number(item?.attraction ?? item?.id ?? 0))
    .filter((id) => Number.isFinite(id) && id > 0)
}

export const upsellMatchesCart = (offer = {}, items = []) => {
  const triggerIds = resolveUpsellTriggerIds(offer)
  const cartAttractionIds = resolveUpsellCartAttractionIds(items)

  if (!triggerIds.length || !cartAttractionIds.length) {
    return false
  }

  return triggerIds.some((id) => cartAttractionIds.includes(id))
}

export const upsellAppliesToCart = (offer = {}, items = []) => {
  if (!upsellMatchesCart(offer, items)) {
    return false
  }

  const addOnAttractionId = Number(offer?.add_on_attraction_id ?? 0)

  if (!addOnAttractionId) {
    return false
  }

  return resolveUpsellCartAttractionIds(items).includes(addOnAttractionId)
}

export const buildSelectedUpsellPayload = (offers = [], items = []) => {
  return offers
    .filter((offer) => upsellAppliesToCart(offer, items))
    .map((offer) => ({ id: resolveUpsellId(offer) }))
    .filter((offer) => offer.id > 0)
}

const parseDiscountNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = Number(String(value).replace(/[^0-9.-]/g, ''))

  return Number.isFinite(parsed) ? parsed : null
}

const firstDiscountNumber = (offer = {}, keys = [], predicate = () => true) => {
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(offer, key) || !predicate(offer[key])) {
      continue
    }

    const value = parseDiscountNumber(offer[key])

    if (value !== null) {
      return value
    }
  }

  return null
}

const resolveDiscountType = (offer = {}) => String(offer.discount_type ?? offer.discountType ?? '').toLowerCase()
const isPercentValue = (value) => String(value ?? '').includes('%')

const resolveFixedDiscount = (offer = {}) => {
  const explicitAmount = firstDiscountNumber(offer, [
    'discount_amount',
    'discountAmount',
    'amount_off',
    'amountOff',
    'fixed_discount',
    'fixedDiscount',
  ], (value) => !isPercentValue(value))

  if (explicitAmount !== null) {
    return explicitAmount
  }

  const discountType = resolveDiscountType(offer)

  if (discountType && !discountType.includes('percent')) {
    return firstDiscountNumber(offer, ['discount_value', 'discountValue'], (value) => !isPercentValue(value))
  }

  return null
}

const resolvePercentDiscount = (offer = {}) => {
  const percentFromAmount = firstDiscountNumber(offer, ['discount_amount', 'discountAmount'], isPercentValue)

  if (percentFromAmount !== null) {
    return percentFromAmount
  }

  const explicitPercent = firstDiscountNumber(offer, [
    'discount_percent',
    'discountPercent',
    'discount_percentage',
    'discountPercentage',
    'percent_off',
    'percentOff',
  ])

  if (explicitPercent !== null) {
    return explicitPercent
  }

  const discountType = resolveDiscountType(offer)

  if (discountType.includes('percent')) {
    const typedPercent = firstDiscountNumber(offer, ['discount_value', 'discountValue'])

    if (typedPercent !== null) {
      return typedPercent
    }
  }

  const textMatch = `${offer.title ?? ''} ${offer.description ?? ''}`.match(/(\d+(?:\.\d+)?)\s*%\s*off/i)

  return textMatch ? Number(textMatch[1]) : null
}

const resolveOfferTicketPrices = (offer = {}) => {
  const prices = new Map()
  const text = `${offer.title ?? ''} ${offer.description ?? ''}`
  const pricePattern = /\$?\s*([0-9]+(?:\.[0-9]+)?)\s*(?:AUD)?\s*(Adults?|Children|Child|Seniors?|Infants?|Spectators?)/gi
  let match = pricePattern.exec(text)

  while (match) {
    prices.set(normaliseParticipantKey(match[2]), Number(match[1]))
    match = pricePattern.exec(text)
  }

  return prices
}

const calculateTicketPriceDiscount = (offer = {}, addOnItems = []) => {
  const offerPrices = resolveOfferTicketPrices(offer)

  if (!offerPrices.size) {
    return null
  }

  const discount = addOnItems.reduce((total, item) => {
    return total + Object.entries(item.participants ?? {}).reduce((itemTotal, [key, count]) => {
      const participantKey = normaliseParticipantKey(key)
      const offerPrice = offerPrices.get(participantKey)

      if (offerPrice === undefined) {
        return itemTotal
      }

      const ticket = findTicketOption(item, participantKey)
      const ticketPrice = Number(ticket?.price ?? ticket?.retailPrice ?? 0)
      const ticketDiscount = Math.max(0, ticketPrice - offerPrice)

      return itemTotal + (ticketDiscount * Number(count || 0))
    }, 0)
  }, 0)

  return discount > 0 ? discount : null
}

const dateKey = (value) => String(value ?? '').slice(0, 10)
const shouldCheckSameDay = (offer = {}) => [true, 1, '1', 'true'].includes(
  typeof offer.check_same_day_availability === 'string'
    ? offer.check_same_day_availability.toLowerCase()
    : offer.check_same_day_availability
)

const addOnItemsForOffer = (offer = {}, items = []) => {
  const addOnAttractionId = Number(offer.add_on_attraction_id ?? 0)
  const addOnItems = items.filter((item) => Number(item.attraction ?? 0) === addOnAttractionId)

  if (!shouldCheckSameDay(offer)) {
    return addOnItems
  }

  const triggerIds = resolveUpsellTriggerIds(offer)

  return addOnItems.filter((addOnItem) => items.some((item) => (
    triggerIds.includes(Number(item.attraction ?? 0)) && dateKey(item.time) === dateKey(addOnItem.time)
  )))
}

const calculateLocalUpsellDiscount = (offer = {}, addOnItems = []) => {
  const addOnSubtotal = addOnItems.reduce((total, item) => total + getItemTotal(item), 0)

  if (addOnSubtotal <= 0) {
    return 0
  }

  const fixedDiscount = resolveFixedDiscount(offer)

  if (fixedDiscount !== null) {
    return Math.min(addOnSubtotal, fixedDiscount)
  }

  const percentDiscount = resolvePercentDiscount(offer)

  if (percentDiscount !== null) {
    return Math.min(addOnSubtotal, addOnSubtotal * (percentDiscount / 100))
  }

  const ticketPriceDiscount = calculateTicketPriceDiscount(offer, addOnItems)

  if (ticketPriceDiscount !== null) {
    return Math.min(addOnSubtotal, ticketPriceDiscount)
  }

  return 0
}

export const calculateLocalUpsellDiscounts = (offers = [], items = []) => {
  return offers
    .filter((offer) => upsellAppliesToCart(offer, items))
    .map((offer, index) => {
      const discountValue = calculateLocalUpsellDiscount(offer, addOnItemsForOffer(offer, items))

      if (discountValue <= 0) {
        return null
      }

      return {
        id: resolveUpsellId(offer) || index,
        title: offer.discount_title ?? offer.discountTitle ?? offer.title ?? 'Total discount',
        discount_value: discountValue,
      }
    })
    .filter(Boolean)
}

export const formatUpsellDiscount = (offer = {}) => {
  const value = String(offer?.discount_amount ?? '').trim()

  if (!value) {
    return ''
  }

  return value.startsWith('$') || value.endsWith('%') ? value : `$${value}`
}

export const resolveUpsellAttraction = (offer = {}) => {
  const code = offer?.add_on_attraction?.code;

  const experiences = window.bookingWidget?.experiences ?? [];

  return experiences.find((experience) => experience.code === code) || null
}

export const resolveUpsellHref = (offer = {}) => {
  const attraction = resolveUpsellAttraction(offer)

  const directUrl = attraction?.booking_link
    || attraction?.link
    || attraction?.url
    || attraction?.permalink
    || attraction?.page_url

  if (directUrl) {
    return directUrl.includes('#booking-form') ? directUrl : `${directUrl.replace(/\/$/, '')}/#booking-form`
  }

  if (attraction?.slug) {
    return `/visit-experiences/${attraction.slug}/#booking-form`
  }

  return null
}

export const resolveUpsellButtonLabel = (offer = {}) => {
  return `Select Tickets`

  const attraction = resolveUpsellAttraction(offer)
  const title = attraction?.retail_title || attraction?.title || attraction?.name || 'this experience'
  return `Add tickets to ${title}`
}
